import {HttpErrorResponse} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {defer, EMPTY, from, Observable, throwError} from 'rxjs';
import {catchError, finalize, switchMapTo, tap} from 'rxjs/operators';
import {apiErrorArrayIncludes} from '../../common/api-error-array';
import {HTTP_STATUS} from '../../common/api-helpers';
import {REQUESTED_URL_KEY} from '../../common/constants';
import {AuthService} from '../../components/auth/auth.service';
import {TWO_FACTOR_AUTH_ERRORS, TwoFactorAuthError} from '../../enums/two-factor-auth-error';
import {ROUTE_REDIRECTS_TOKEN, RouteRedirects} from '../../models/route-redirects';
import {LocalStorageService} from '../../services/local-storage.service';
import {TwoFactorAuthLoginPageGuard} from '../two-factor-auth-login-page';

interface AuthCredentials {
    username: string;
    password: string;
}

const TWO_FACTOR_AUTH_ROUTE_PATH_SEGMENT = '2fa';

@Injectable()
export class LoginTwoFAService {

    private static isTwoFactorAuthRequiredResponse(response: HttpErrorResponse): boolean {
        return response.status === HTTP_STATUS.UNAUTHORIZED
            && apiErrorArrayIncludes(response.error, TwoFactorAuthError.TWO_FA_CODE_IS_EMPTY);
    }

    private static isTwoFactorAuthWrongCodeResponse(response: HttpErrorResponse): boolean {
        return response.status === HTTP_STATUS.UNAUTHORIZED
            && apiErrorArrayIncludes(response.error, ...TWO_FACTOR_AUTH_ERRORS);
    }

    private credentials: AuthCredentials;

    constructor(
        private authService: AuthService,
        private router: Router,
        private twoFactorAuthLoginPageGuard: TwoFactorAuthLoginPageGuard,
        private localStorageService: LocalStorageService,

        @Inject(ROUTE_REDIRECTS_TOKEN)
        private routeRedirects: RouteRedirects
    ) {}

    public authWithCredentials(username: string, password: string): Observable<any> {
        return defer(() => {
            this.credentials = {username, password};

            return this.authService
                .authorizeWithCredentials(username, password)
                .pipe(
                    tap(() => this.unlockApp()),
                    catchError(error => error instanceof HttpErrorResponse
                        ? this.handleAuthWithCredentialsError(error)
                        : throwError(error)
                    )
                );
        });
    }

    public authWithTwoFACode(twoFACode: string): Observable<any> {
        return defer(() => {
            const {username, password} = this.credentials;

            return this.authService
                .authorizeWithCredentials(username, password, twoFACode)
                .pipe(
                    tap(() => this.unlockApp()),
                    catchError(error => error instanceof HttpErrorResponse
                        ? this.handleAuthWithTwoFACode(error)
                        : throwError(error)
                    )
                );
        });
    }

    private handleAuthWithCredentialsError(errorResponse: HttpErrorResponse): Observable<any> {
        return LoginTwoFAService.isTwoFactorAuthRequiredResponse(errorResponse as HttpErrorResponse)
            ? this.navigateToTwoFACodePage()
            : throwError(errorResponse);
    }

    private handleAuthWithTwoFACode(errorResponse: HttpErrorResponse): Observable<any> {
        return LoginTwoFAService.isTwoFactorAuthWrongCodeResponse(errorResponse as HttpErrorResponse)
            ? throwError(errorResponse)
            : this.handleAuthWithTwoFACodeWrongCredentials();
    }

    private navigateToTwoFACodePage(): Observable<never> {
        return defer(() => {
            this.twoFactorAuthLoginPageGuard.setTwoFACodeNavigationAllowed(true);

            const navigationPromise = this.router.navigate([
                this.routeRedirects.LOGIN_ROUTE,
                TWO_FACTOR_AUTH_ROUTE_PATH_SEGMENT
            ]);

            return from(navigationPromise).pipe(
                finalize(() => this.twoFactorAuthLoginPageGuard.setTwoFACodeNavigationAllowed(false)),
                switchMapTo(EMPTY)
            );
        });
    }

    private handleAuthWithTwoFACodeWrongCredentials(): Observable<any> {
        return defer(() => {
            this.credentials = null;
            this.authService.innerLogout();

            return EMPTY;
        });
    }

    private unlockApp(): void {
        const requestedUrl = this.localStorageService.get(REQUESTED_URL_KEY, '');

        if (requestedUrl) {
            this.localStorageService.remove(REQUESTED_URL_KEY);
        }

        this.router.navigate([requestedUrl || this.routeRedirects.DASHBOARD_ROUTES[this.authService.userRole]]);
    }

}
